import React, { useState } from 'react';

interface Asset {
  cash: number;
  stockCount: number;
  totalPurchase: number;
  realizedProfit: number;
}

interface MockInvestProps {
  currentPrice: number;
  prevPrice: number;
  isActive: boolean;
  currentStep: number;
  totalSteps: number;
  onNextStep: () => void;
}

export const MockInvest: React.FC<MockInvestProps> = ({ 
  currentPrice, prevPrice, isActive, currentStep, totalSteps, onNextStep 
}) => {
  const INITIAL_CASH = 1000000; // 초기 자본 100만원
  const [asset, setAsset] = useState<Asset>({
    cash: INITIAL_CASH,
    stockCount: 0,
    totalPurchase: 0,
    realizedProfit: 0,
  });

  const [modalMode, setModalMode] = useState<'BUY' | 'SELL' | null>(null);
  const [tradeQuantity, setTradeQuantity] = useState(0);

  // --- 수치 계산 로직 ---
  // 현재가가 0일 경우(데이터 로드 전)를 대비한 방어 코드
  const displayPrice = currentPrice || 0;
  
  // 등락률 (전일 대비)
  const priceDiffPercent = prevPrice > 0 ? ((displayPrice - prevPrice) / prevPrice) * 100 : 0;
  
  // 평단가 계산
  const avgPrice = asset.stockCount > 0 ? asset.totalPurchase / asset.stockCount : 0;
  
  // 평가 금액 및 손익
  const totalEvaluation = asset.stockCount * displayPrice;
  const evaluationProfit = asset.stockCount > 0 ? totalEvaluation - asset.totalPurchase : 0;
  const evaluationRate = asset.totalPurchase > 0 ? (evaluationProfit / asset.totalPurchase) * 100 : 0;
  
  // 총 추정 자산 (현금 + 주식 평가금)
  const estimatedAsset = asset.cash + totalEvaluation;
  
  // 누적 수익률 (초기 자본 대비)
  const totalReturnRate = ((estimatedAsset - INITIAL_CASH) / INITIAL_CASH) * 100;

  // 매수/매도 가능 수량
  const maxBuyable = displayPrice > 0 ? Math.floor(asset.cash / displayPrice) : 0;
  const maxSellable = asset.stockCount;

  const handleTrade = () => {
    if (modalMode === 'BUY') {
      const cost = tradeQuantity * displayPrice;
      if (cost > asset.cash) return alert("현금이 부족합니다.");
      setAsset(prev => ({ 
        ...prev, 
        cash: prev.cash - cost, 
        stockCount: prev.stockCount + tradeQuantity, 
        totalPurchase: prev.totalPurchase + cost 
      }));
    } else {
      const revenue = tradeQuantity * displayPrice;
      const purchaseCostOfSold = avgPrice * tradeQuantity;
      setAsset(prev => ({ 
        ...prev, 
        cash: prev.cash + revenue, 
        stockCount: prev.stockCount - tradeQuantity, 
        totalPurchase: prev.totalPurchase - purchaseCostOfSold, 
        realizedProfit: prev.realizedProfit + (revenue - purchaseCostOfSold) 
      }));
    }
    setModalMode(null);
    setTradeQuantity(0);
  };

  if (!isActive) {
    return (
      <div style={{ width: '380px', height: '782px', backgroundColor: '#f5f5f5', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', border: '1px solid #ddd', textAlign: 'center', padding: '20px' }}>
        튜토리얼 단계입니다.<br/>가이드를 따라 학습을 완료해주세요.
      </div>
    );
  }

  return (
    <div style={{ width: '380px', height: '782px', backgroundColor: '#fff', borderRadius: '16px', border: '1px solid #ddd', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      
      {/* 1. 상단 액션 버튼 */}
      <div style={{ padding: '20px', borderBottom: '1px solid #eee' }}>
        <button 
          onClick={onNextStep} 
          style={{ 
            width: '100%', padding: '16px', 
            backgroundColor: currentStep >= totalSteps ? '#00c853' : '#000', 
            color: '#fff', border: 'none', borderRadius: '12px', fontWeight: 'bold', fontSize: '15px', cursor: 'pointer',
            transition: 'all 0.2s'
          }}
        >
          {currentStep >= totalSteps ? '🏆 최종 결과 확인' : '다음 날짜로 이동 →'}
        </button>
      </div>

      <div style={{ padding: '20px', flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* 2. 진행 상태 */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '6px', color: '#666' }}>
            <span>진행률</span><span>{Math.min(currentStep, totalSteps)} / {totalSteps} 턴</span>
          </div>
          <div style={{ height: '4px', backgroundColor: '#eee', borderRadius: '2px' }}>
            <div style={{ width: `${(Math.min(currentStep, totalSteps) / totalSteps) * 100}%`, height: '100%', backgroundColor: '#000', borderRadius: '2px', transition: 'width 0.3s' }} />
          </div>
        </div>

        {/* 3. 현재가 시세 정보 */}
        <div style={{ padding: '20px', backgroundColor: '#fcfcfc', border: '1px solid #f0f0f0', borderRadius: '12px', textAlign: 'center', marginBottom: '20px' }}>
          <div style={{ fontSize: '13px', color: '#888', marginBottom: '4px' }}>현재 주가</div>
          <div style={{ fontSize: '26px', fontWeight: '800', color: displayPrice >= prevPrice ? '#ef5350' : '#3686ff' }}>
            {displayPrice.toLocaleString()}원
            <span style={{ fontSize: '14px', marginLeft: '6px', fontWeight: '500' }}>
              ({priceDiffPercent >= 0 ? '+' : ''}{priceDiffPercent.toFixed(2)}%)
            </span>
          </div>
        </div>

        {/* 4. 매수/매도 실행 버튼 */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '25px' }}>
          <button 
            onClick={() => setModalMode('BUY')} 
            disabled={currentStep >= totalSteps}
            style={{ flex: 1, padding: '14px', backgroundColor: '#ef5350', color: '#fff', border: 'none', borderRadius: '10px', fontWeight: 'bold', cursor: 'pointer', opacity: currentStep >= totalSteps ? 0.5 : 1 }}
          >매수</button>
          <button 
            onClick={() => setModalMode('SELL')} 
            disabled={currentStep >= totalSteps}
            style={{ flex: 1, padding: '14px', backgroundColor: '#3686ff', color: '#fff', border: 'none', borderRadius: '10px', fontWeight: 'bold', cursor: 'pointer', opacity: currentStep >= totalSteps ? 0.5 : 1 }}
          >매도</button>
        </div>

        {/* 5. 자산 현황 상세 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <DetailRow 
            label="추정 자산" 
            value={`${Math.floor(estimatedAsset).toLocaleString()}원`} 
            subValue={`${totalReturnRate.toFixed(2)}%`} 
            bold 
            color={totalReturnRate >= 0 ? '#ef5350' : '#3686ff'}
          />
          <hr style={{ border: 'none', borderTop: '1px solid #f5f5f5', margin: '4px 0' }} />
          <DetailRow label="보유 현금" value={`${Math.floor(asset.cash).toLocaleString()}원`} />
          <DetailRow 
            label="평가 손익" 
            value={`${Math.floor(evaluationProfit).toLocaleString()}원`} 
            subValue={`${evaluationRate.toFixed(2)}%`} 
            color={evaluationProfit >= 0 ? '#ef5350' : '#3686ff'} 
          />
          <DetailRow label="평균 단가" value={`${Math.floor(avgPrice).toLocaleString()}원`} />
          <DetailRow label="보유 주식" value={`${asset.stockCount}주`} />
        </div>
      </div>

      {/* 6. 거래 모달 */}
      {modalMode && (
        <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 10, backdropFilter: 'blur(4px)', borderRadius: '16px' }}>
          <div style={{ backgroundColor: '#fff', padding: '24px', borderRadius: '20px', width: '90%', boxShadow: '0 10px 25px rgba(0,0,0,0.1)' }}>
            <h2 style={{ margin: '0 0 16px 0', fontSize: '18px', textAlign: 'center' }}>
              {modalMode === 'BUY' ? '주문: 매수' : '주문: 매도'}
            </h2>
            
            <div style={{ backgroundColor: '#f5f5f5', padding: '16px', borderRadius: '12px', marginBottom: '16px' }}>
              <div style={{ fontSize: '12px', color: '#888', marginBottom: '4px' }}>주문 수량</div>
              <div style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '10px' }}>{tradeQuantity}주</div>
              <input 
                type="range" 
                min="0" 
                max={modalMode === 'BUY' ? maxBuyable : maxSellable} 
                value={tradeQuantity} 
                onChange={(e) => setTradeQuantity(Number(e.target.value))} 
                style={{ width: '100%', accentColor: '#000' }} 
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
              <span style={{ color: '#888', fontSize: '14px' }}>결제 예정 금액</span>
              <span style={{ fontWeight: 'bold' }}>{(tradeQuantity * displayPrice).toLocaleString()}원</span>
            </div>

            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => {setModalMode(null); setTradeQuantity(0);}} style={{ flex: 1, padding: '12px', borderRadius: '10px', border: '1px solid #eee', background: '#fff', cursor: 'pointer' }}>취소</button>
              <button onClick={handleTrade} style={{ flex: 1, padding: '12px', borderRadius: '10px', border: 'none', background: '#000', color: '#fff', cursor: 'pointer', fontWeight: 'bold' }}>확정</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const DetailRow = ({ label, value, subValue, color, bold }: any) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
    <span style={{ color: '#888', fontSize: '14px' }}>{label}</span>
    <div style={{ textAlign: 'right' }}>
      <div style={{ fontWeight: bold ? '800' : '600', color: color || '#1a1a1a', fontSize: '15px' }}>{value}</div>
      {subValue && <div style={{ fontSize: '12px', color: color || '#aaa', marginTop: '2px' }}>({subValue})</div>}
    </div>
  </div>
);